import React, { useState, Fragment } from 'react';
import { Transition, TransitionChild } from '@headlessui/react';
import {
  RectangleGroupIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import SpaceRow from 'components/SpaceRow';
import FeeBoundsTooltip from 'components/FeeBoundsTooltip';
import { useMediaQuery } from 'hooks/useMediaQuery';
import { computeBookingPricing } from 'utils/bookingPricing';
import { t } from 'i18next';

const BookingBubble = ({
  action,
  isSticky,
  isBrand,
  selectedPlace,
  lowestPrice,
  bookingAction,
  spaceList,
  onSelect,
  brands = [],
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [showBreakdown, setShowBreakdown] = useState(false);
  const isMobile = useMediaQuery('(max-width: 1024px)');

  const toggleExpanded = () => setIsExpanded(!isExpanded);
  const toggleBreakdown = () => setShowBreakdown(!showBreakdown);

  const pricing = computeBookingPricing(selectedPlace?.price);
  const total =
    selectedPlace && selectedPlace.price ? pricing.total : null;

  const renderPricing = () => {
    if (!isBrand || !selectedPlace) return null;

    return (
      <div className="my-6 border-t border-b border-gray-300 flex flex-col items-center w-full space-y-2">
        <p className="pt-4 text-xs font-thin">
          {t('event_detail.bubble_selection')}
        </p>
        <div className="flex flex-row w-full items-center">
          <p className="text-md font-bold">{t('event_detail.plan')}</p>
          <p className="ml-4 text-md font-bold text-yellow-02 border-r border-gray-300 pr-2">
            {selectedPlace.name}
          </p>
          <p className="ml-2 text-sm text-yellow-02">
            {selectedPlace.size}
            {t('event_detail.m2')}
          </p>
        </div>
        <Transition
          show={showBreakdown}
          enter="transition-opacity duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
        >
          <div className="w-full">
            <div className="flex justify-between">
              <span className="text-gray-500">{t('common.price')}</span>
              <span className="text-gray-500">
                {Number(selectedPlace.price).toFixed(2)}€
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-500">
                {t('event_detail.t2b_fee')}
                <FeeBoundsTooltip className="ml-1" />
              </span>
              <span className="text-gray-500">
                {pricing.t2bFee.toFixed(2)}€
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-500">{t('event_detail.vat')}</span>
              <span className="text-gray-500">
                {pricing.vat.toFixed(2)}€
              </span>
            </div>
          </div>
        </Transition>
        {total && (
          <div
            className="flex pb-4 justify-between w-full font-bold cursor-pointer"
            onClick={toggleBreakdown}
          >
            <span>Total</span>
            <div className="flex items-center space-x-2">
              {showBreakdown ? (
                <ChevronUpIcon className="w-4 h-4 text-yellow-500" />
              ) : (
                <ChevronDownIcon className="w-4 h-4 text-yellow-500" />
              )}
              <span>{total.toFixed(2)}€</span>
            </div>
          </div>
        )}
      </div>
    );
  };

  if (isMobile) {
    return (
      <>
        {!isExpanded ? (
          <button
            onClick={toggleExpanded}
            className="fixed z-50 bottom-5 right-5 p-4 font-semibold text-lg bg-yellow-05 text-white rounded-full shadow-2xl hover:bg-yellow-500 transition"
          >
            {t('event_detail.book')}
          </button>
        ) : (
          <Transition show={isExpanded} as={Fragment}>
            {/* Backdrop with fade */}
            <TransitionChild
              as="div"
              enter="transition-opacity duration-300"
              enterFrom="opacity-0"
              enterTo="opacity-100"
              leave="transition-opacity duration-200"
              leaveFrom="opacity-100"
              leaveTo="opacity-0"
              className="fixed inset-0 bg-black bg-opacity-60 z-40"
              onClick={toggleExpanded}
            />

            {/* Panel with slide-up */}
            <TransitionChild
              as="div"
              enter="transition ease-out duration-300 transform"
              enterFrom="translate-y-full"
              enterTo="translate-y-0"
              leave="transition ease-in duration-200 transform"
              leaveFrom="translate-y-0"
              leaveTo="translate-y-full"
              className="fixed z-50 bottom-0 left-0 right-0 bg-white rounded-t-2xl p-5 shadow-2xl max-h-[90vh] flex flex-col overflow-y-auto scrolling-touch overscroll-contain"
            >
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-xl font-semibold text-gray-800">
                  {t('event_detail.book_place')}
                </h2>
                <button onClick={toggleExpanded} aria-label="Close panel">
                  <XMarkIcon className="w-6 h-6 text-gray-500 hover:text-gray-700" />
                </button>
              </div>

              <div className="flex-grow space-y-4 mb-6">
                {spaceList?.map((space) => (
                  <SpaceRow
                    space={space}
                    key={space.id}
                    active={selectedPlace?.id === space.id}
                    onClick={() => onSelect(space)}
                  />
                ))}
              </div>

              {renderPricing()}

              <button
                onClick={bookingAction}
                disabled={!selectedPlace}
                className={`w-full py-3 rounded-full text-base font-semibold transition ${
                  selectedPlace
                    ? 'bg-yellow-05 text-white hover:bg-yellow-600'
                    : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                }`}
              >
                {t('event_detail.book_place')}
              </button>
              <p className="text-xs text-center text-gray-500 mt-2">
                {t('event_detail.no_charge')}
              </p>
            </TransitionChild>
          </Transition>
        )}
      </>
    );
  }

  return (
    <div
      className={`flex flex-col items-center p-6 bg-white rounded-3xl shadow-xl z-20 ${isSticky ? 'sticky top-[92px] xl:top-[116px] 2xl:top-[128px]' : ''}`}
    >
      <h3 className="text-lg font-semibold mb-6">
        {t('common.from_price')} {lowestPrice}€{t('event_detail.per_space')}
      </h3>
      <div className="flex flex-col items-stretch w-full">
        {/* One entry point: the space modal shows the list and, when the event
            has one, the floorplan side by side. */}
        <button
          type="button"
          className="flex justify-center items-center gap-2 py-2 px-4 border-2 border-yellow-03 text-gray-600 rounded-full hover:bg-yellow-03 hover:text-white"
          onClick={() => action()}
        >
          <span>{t('event_detail.select_space')}</span>
          <RectangleGroupIcon className="w-6 h-6 shrink-0" />
        </button>
      </div>
      {renderPricing()}
      <button
        onClick={bookingAction}
        disabled={!selectedPlace}
        className={`shadow-lg py-5 mt-4 px-6 text-xl rounded-full transition ${
          selectedPlace
            ? 'bg-yellow-05 text-white hover:bg-yellow-02'
            : 'bg-gray-300 text-gray-500 cursor-not-allowed shadow-none'
        }`}
      >
        {t('event_detail.book_place')}
      </button>
      <p className="text-xs text-center text-gray-600 mt-2">
        {t('event_detail.no_charge')}
      </p>

      {brands.length > 0 && (
        <>
          <hr className="my-4 border-gray-200" />
          <p className="text-center text-sm text-gray-400 font-semibold mb-3">
            {t('event_detail.confirmed_brands')}
          </p>
          <div className="flex items-center justify-center gap-2 flex-wrap">
            {brands.slice(0, 4).map((b, idx) => (
              <div
                key={b.id ?? `${b.username || b.name || 'brand'}-${idx}`}
                className="w-14 h-14 shrink-0 rounded-full overflow-hidden ring-1 ring-gray-200 bg-white"
                title={b.username || b.name}
              >
                <img
                  src={b.logo || b.avatar || b.image}
                  alt={b.username || b.name || 'brand'}
                  className="w-full h-full object-cover"
                  loading="lazy"
                  draggable={false}
                />
              </div>
            ))}
            {brands.length > 4 && (
              <div className="w-14 h-14 rounded-full flex items-center justify-center text-gray-400 text-2xl font-light">
                +{brands.length - 4}
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default BookingBubble;
